import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, ShoppingBag, CalendarDays, Compass, ArrowLeft, Sparkles, Library } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { TemplateCard } from '../components/TemplateCard';
interface TemplateCurationScreenProps {
  onNext: () => void;
  onBack: () => void;
  onPhaseChange?: (phase: 'question' | 'results') => void;
}
export function TemplateCurationScreen({
  onNext,
  onBack,
  onPhaseChange
}: TemplateCurationScreenProps) {
  const [phase, setPhase] = useState<'question' | 'results'>('question');
  const [selectedGoal, setSelectedGoal] = useState<string>('leads');
  // Notify parent of phase changes
  useEffect(() => {
    if (onPhaseChange) {
      onPhaseChange(phase);
    }
  }, [phase, onPhaseChange]);
  const handleGoalSelect = (goal: string) => {
    setSelectedGoal(goal);
    setTimeout(() => setPhase('results'), 300);
  };
  const goalContent: Record<string, {
    subheading: string;
    templates: {
      title: string;
      description: string;
      imageColor: string;
    }[];
  }> = {
    leads: {
      subheading: 'How do you want to collect leads?',
      templates: [{
        title: 'Email list registration',
        description: 'Simple way for someone to join your email list.',
        imageColor: 'bg-[#3b82f6]/10'
      }, {
        title: 'Give away something free',
        description: 'Offer a resource like a guide or checklist in exchange for an email address.',
        imageColor: 'bg-[#8b5cf6]/10'
      }, {
        title: 'Personalized quiz',
        description: 'Ask a few questions and give custom results in exchange for contact info.',
        imageColor: 'bg-[#10b981]/10'
      }, {
        title: 'Educational video',
        description: 'Share a short video with valuable tips, then ask viewers to sign up for more.',
        imageColor: 'bg-[#ec4899]/10'
      }]
    },
    sell: {
      subheading: 'How do you want to sell?',
      templates: [{
        title: 'Explain and sell on one page',
        description: 'A simple page that shows your product (digital or physical) and lets people buy immediately.',
        imageColor: 'bg-[#3b82f6]/10'
      }, {
        title: 'Use a video to sell',
        description: "Record a video explaining what you're selling, then send viewers straight to checkout.",
        imageColor: 'bg-[#8b5cf6]/10'
      }, {
        title: 'Qualify buyers with an application',
        description: 'Let people apply to work with you so you can screen them before selling your high-ticket program.',
        imageColor: 'bg-[#10b981]/10'
      }, {
        title: "Promote someone else's product",
        description: "Send people to another company's offer and earn a commission as an affiliate.",
        imageColor: 'bg-[#ec4899]/10'
      }]
    },
    event: {
      subheading: 'What kind of event are you hosting?',
      templates: [{
        title: 'Live webinar or training',
        description: 'Promote your virtual or in-person event and collect registrations.',
        imageColor: 'bg-[#3b82f6]/10'
      }, {
        title: 'Pre-recorded webinar on autopilot',
        description: 'Run a webinar that plays on a set schedule and sells your offer without you being live.',
        imageColor: 'bg-[#8b5cf6]/10'
      }, {
        title: 'Multi-day challenge',
        description: 'Host a challenge where people sign up and receive daily tasks or lessons.',
        imageColor: 'bg-[#10b981]/10'
      }, {
        title: 'Virtual summit with speakers',
        description: 'Promote a live event with multiple presenters and collect signups.',
        imageColor: 'bg-[#ec4899]/10'
      }]
    },
    browse: {
      subheading: 'How do you want to collect leads?',
      templates: [{
        title: 'Email list registration',
        description: 'Simple way for someone to join your email list.',
        imageColor: 'bg-[#3b82f6]/10'
      }, {
        title: 'Give away something free',
        description: 'Offer a resource like a guide or checklist in exchange for an email address.',
        imageColor: 'bg-[#8b5cf6]/10'
      }, {
        title: 'Personalized quiz',
        description: 'Ask a few questions and give custom results in exchange for contact info.',
        imageColor: 'bg-[#10b981]/10'
      }, {
        title: 'Educational video',
        description: 'Share a short video with valuable tips, then ask viewers to sign up for more.',
        imageColor: 'bg-[#ec4899]/10'
      }]
    }
  };
  const currentGoal = goalContent[selectedGoal] || goalContent.leads;
  return <div className="min-h-screen flex flex-col p-6 pb-20 bg-[var(--bg)] transition-colors duration-300" data-id="element-161">
      <div className="flex-1 flex flex-col items-center justify-center max-w-6xl mx-auto w-full" data-id="element-162">
        <AnimatePresence mode="wait" data-id="element-163">
          {phase === 'question' ? <motion.div key="question" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} className="w-full max-w-4xl" data-id="element-164">
              <div className="text-center mb-12" data-id="element-165">
                <h2 className="text-4xl font-bold text-[var(--text-primary)] mb-4 transition-colors duration-300" data-id="element-166">
                  What do you want to do first?
                </h2>
                <p className="text-[var(--text-secondary)] transition-colors duration-300" data-id="element-167">
                  We'll recommend the best templates for your goal.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-168">
                {[{
              id: 'leads',
              label: 'Collect leads',
              description: "Best for growing your list or if you're still deciding what to offer",
              icon: Users
            }, {
              id: 'sell',
              label: 'Sell a product',
              description: 'Best for monetizing an existing list with a product, course, or service',
              icon: ShoppingBag
            }, {
              id: 'event',
              label: 'Host an event',
              description: 'Best for running webinars, workshops, challenges or in-person events',
              icon: CalendarDays
            }, {
              id: 'browse',
              label: 'Just browsing',
              description: 'Best for exploring the full template library on your own',
              icon: Compass
            }].map((option, idx) => <motion.button key={option.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} onClick={() => handleGoalSelect(option.id)} className="flex items-start p-6 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl hover:border-[var(--accent-gold)] hover:bg-[var(--goal-hover-bg)] transition-all group text-left shadow-sm" data-id="element-169">
                    <div className="p-3 bg-[var(--bg-surface)] rounded-lg text-[var(--text-secondary)] group-hover:text-[var(--accent-gold)] group-hover:bg-[var(--accent-gold)]/10 transition-colors mr-4 shrink-0" data-id="element-170">
                      <option.icon size={24} data-id="element-171" />
                    </div>
                    <div data-id="element-172">
                      <span className="text-lg font-medium text-[var(--text-primary)] group-hover:text-[var(--text-primary)] transition-colors duration-300 block" data-id="element-173">
                        {option.label}
                      </span>
                      <span className="text-sm text-[var(--text-secondary)] mt-1 block leading-relaxed" data-id="element-174">
                        {option.description}
                      </span>
                    </div>
                  </motion.button>)}
              </div>

              {/* Back Button - below the cards */}
              <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="flex justify-center mt-8" data-id="element-175">
                <button onClick={onBack} className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] text-sm font-medium transition-colors" data-id="element-176">
                  <ArrowLeft size={16} data-id="element-177" />
                  Back
                </button>
              </motion.div>
            </motion.div> : <motion.div key="results" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="w-full" data-id="element-178">
              <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4" data-id="element-179">
                <div data-id="element-180">
                  <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.1
              }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--accent-gold)]/10 border border-[var(--accent-gold)]/20 text-[var(--icon-accent-amber)] text-xs font-medium mb-3" data-id="element-181">
                    <Sparkles size={12} data-id="element-182" />
                    <span data-id="element-183">Curated from our library</span>
                  </motion.div>
                  <h2 className="text-3xl font-bold text-[var(--text-primary)] mb-2 transition-colors duration-300" data-id="element-184">
                    Handpicked for your goal
                  </h2>
                  <p className="text-[var(--text-secondary)] transition-colors duration-300 max-w-xl" data-id="element-185">
                    {currentGoal.subheading}
                  </p>
                </div>
                <Button variant="ghost" onClick={() => setPhase('question')} leftIcon={<ArrowLeft size={16} data-id="element-187" />} className="shrink-0" data-id="element-186">
                  Edit Goal
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12" data-id="element-188">
                {currentGoal.templates.map((template, idx) => <TemplateCard key={template.title} title={template.title} description={template.description} imageColor={template.imageColor} onClick={onNext} delay={0.1 + idx * 0.1} data-id="element-189" />)}
              </div>

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="p-6 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] flex items-start gap-4 max-w-3xl mx-auto" data-id="element-190">
                <div className="p-2 rounded-lg bg-[var(--bg-card)] text-[var(--accent-gold)] border border-[var(--border)] shadow-sm shrink-0" data-id="element-191">
                  <Library size={20} data-id="element-192" />
                </div>
                <div data-id="element-193">
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed" data-id="element-194">
                    We're frequently adding new templates. We recommend starting
                    with one of the curated options above, but you can always{' '}
                    <a href="#" className="text-[var(--text-accent)] font-medium hover:underline" data-id="element-195">
                      browse the full library
                    </a>{' '}
                    inside your account.
                  </p>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}